import type { ReactNode } from "react";
import { pathFor } from "../../../app/router/routes";
import { LEVELS } from "../../../shared/curriculum";
import { HOME_ART } from "../assets";
import { Reveal } from "../Reveal";
import { SECTION_IDS, type MarketingCopy, type SkillKey } from "../copy";
import { ArrowRightIcon, ChildrenIcon, ClockIcon, StarIcon, TickIcon } from "../icons";
import { CtaLink } from "./Cta";
import { Section, SectionHeading } from "./primitives";

export interface AudiencesProps {
  t: MarketingCopy;
}

/**
 * The three people a school sale involves, side by side: the school that buys,
 * the teacher who runs it, the parent who hears about it at dinner.
 *
 * 🔴 THE SCHOOL CARD IS THE TALL ONE AND THE ONLY ONE WITH A BUTTON. The other
 * two describe what a school is buying *for* them; neither of them is who signs.
 *
 * ⚠️ THE PREVIEW'S NUMBERS ARE ILLUSTRATIVE AND LABELLED "Sample data". The
 * levels it counts against are the real `LEVELS` length, so "Level 2 of 3" can
 * never describe a level the product does not have.
 */
export function Audiences({ t }: AudiencesProps) {
  const a = t.audience;

  return (
    <Section id={SECTION_IDS.schools} className="pt-24 lg:pt-32">
      <SectionHeading label={a.label} heading={a.heading} accentClass="bg-success" labelClass="text-success" />

      <div className="mt-10 grid gap-5 lg:grid-cols-2 lg:grid-rows-[auto_auto] lg:gap-6">
        {/* ── For schools ───────────────────────────────────────────────── */}
        <Reveal className="lg:row-span-2">
          <article className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-success/20 bg-success/8 p-7 sm:p-10">
            <Tag className="bg-surface text-success">{a.schools.tag}</Tag>
            <h3 className="mt-4 max-w-[16ch] font-dash text-dash-2xl font-extrabold text-ink">{a.schools.heading}</h3>
            <ul className="mt-6 space-y-3.5">
              {a.schools.points.map((point) => (
                <li key={point} className="flex items-center gap-3 font-dash text-dash-sm font-bold text-ink-soft">
                  <span
                    aria-hidden="true"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-pill border border-success/35 bg-surface text-success"
                  >
                    <TickIcon className="h-4 w-4" />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
            <a
              href={pathFor("for-schools")}
              className="group mt-6 inline-flex w-fit items-center gap-2 rounded-md font-dash text-dash-sm font-extrabold text-success outline-none hover:underline focus-visible:ring-2 focus-visible:ring-success"
            >
              {t.schoolsPage.learnMore}
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-dash group-hover:translate-x-1" />
            </a>
            <div className="mt-8 flex items-end justify-between gap-4 lg:mt-auto lg:pt-10">
              <CtaLink href={pathFor("contact-sales")} variant="primary" size="md">
                {t.cta.bookSchoolDemo}
              </CtaLink>
              <img
                src={HOME_ART.turo}
                alt={a.schools.mascotAlt}
                width={480}
                height={720}
                loading="lazy"
                decoding="async"
                className="kq-float-sm -mb-7 hidden h-56 w-auto object-contain sm:block sm:-mb-10 lg:h-72"
              />
            </div>
          </article>
        </Reveal>

        {/* ── For teachers ──────────────────────────────────────────────── */}
        <Reveal delay={90}>
          <article className="grid h-full gap-6 rounded-2xl border border-line bg-surface p-7 sm:grid-cols-[1fr_1.15fr] sm:p-8">
            <div>
              <Tag className="bg-sky/12 text-ink">{a.teachers.tag}</Tag>
              <h3 className="mt-3 font-dash text-dash-xl font-extrabold text-ink">{a.teachers.heading}</h3>
              <p className="mt-2 font-dash text-dash-sm text-ink-soft">{a.teachers.body}</p>
            </div>
            <ClassOverview t={t} />
          </article>
        </Reveal>

        {/* ── For parents ───────────────────────────────────────────────── */}
        <Reveal delay={180}>
          <article className="grid h-full gap-6 rounded-2xl border border-line bg-surface p-7 sm:grid-cols-[1fr_1.15fr] sm:p-8">
            <div>
              <Tag className="bg-sunshine/18 text-ink">{a.parents.tag}</Tag>
              <h3 className="mt-3 font-dash text-dash-xl font-extrabold text-ink">{a.parents.heading}</h3>
              <p className="mt-2 font-dash text-dash-sm text-ink-soft">{a.parents.body}</p>
            </div>
            <img
              src={HOME_ART.homePlay}
              alt={a.parents.imageAlt}
              width={960}
              height={717}
              loading="lazy"
              decoding="async"
              className="aspect-[4/3] w-full rounded-xl object-cover"
            />
          </article>
        </Reveal>
      </div>

      <Curriculum t={t} />
    </Section>
  );
}

function Tag({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-pill px-3.5 py-1.5 font-dash text-dash-xs font-extrabold ${className}`}>
      {children}
    </span>
  );
}

/** A class at a glance — what a teacher's E1 screen shows, drawn small. */
export function ClassOverview({ t }: { t: MarketingCopy }) {
  const p = t.audience.preview;
  const total = LEVELS.length;
  const rows: { skill: SkillKey; level: number; color: string }[] = [
    { skill: "numeracy", level: 2, color: "var(--color-skill-numeracy)" },
    { skill: "literacy", level: 1, color: "var(--color-skill-literacy)" },
    { skill: "generalAwareness", level: 3, color: "var(--color-skill-general-awareness)" },
  ];

  return (
    <figure className="rounded-xl border border-line bg-cream p-4 shadow-raised">
      <figcaption className="flex items-baseline justify-between gap-3">
        <span className="font-dash text-dash-sm font-extrabold text-ink">{p.caption}</span>
        <span className="font-dash text-dash-xs font-bold text-muted">{t.sampleData}</span>
      </figcaption>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <Stat label={p.students} value="32" icon={<ChildrenIcon className="h-3.5 w-3.5" />} tone="text-success" />
        <Stat label={p.games} value="148" icon={<StarIcon className="h-3.5 w-3.5" />} tone="text-sunshine" />
        <Stat label={p.minutes} value="42m" icon={<ClockIcon className="h-3.5 w-3.5" />} tone="text-teal" />
      </div>
      <div className="mt-3.5 space-y-2.5">
        {rows.map((row) => (
          <div key={row.skill}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-dash text-dash-xs font-bold text-ink">{t.skills[row.skill]}</span>
              <span className="tabular font-dash text-dash-xs font-bold text-muted">{p.levelOf(row.level, total)}</span>
            </div>
            <div className="mt-1 flex gap-1" role="presentation">
              {Array.from({ length: total }, (_, i) => (
                <span
                  key={i}
                  className="h-1.5 flex-1 rounded-pill"
                  style={{ background: i < row.level ? row.color : "var(--color-line)" }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </figure>
  );
}

function Stat({ label, value, icon, tone }: { label: string; value: string; icon: ReactNode; tone: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-2.5 py-2">
      <div className="flex items-center justify-between gap-1">
        <p className="tabular font-dash text-dash-lg font-extrabold text-ink">{value}</p>
        <span aria-hidden="true" className={`shrink-0 ${tone}`}>
          {icon}
        </span>
      </div>
      <p className="font-dash text-dash-xs font-bold whitespace-nowrap text-muted">{label}</p>
    </div>
  );
}

/** Every skill the games build, each in its own token colour. */
const CURRICULUM_SKILLS: { skill: SkillKey; dot: string }[] = [
  { skill: "numeracy", dot: "bg-skill-numeracy" },
  { skill: "literacy", dot: "bg-skill-literacy" },
  { skill: "generalAwareness", dot: "bg-skill-general-awareness" },
  { skill: "socialAwareness", dot: "bg-skill-social-awareness" },
  { skill: "patterns", dot: "bg-skill-matching" },
  { skill: "coordination", dot: "bg-skill-coordination" },
  { skill: "balance", dot: "bg-skill-balance" },
  { skill: "bodyAwareness", dot: "bg-skill-body-awareness" },
];

function Curriculum({ t }: { t: MarketingCopy }) {
  return (
    <Reveal className="mt-10 lg:mt-14">
      <div className="grid items-center gap-8 rounded-2xl bg-canvas p-7 sm:p-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-12">
        <div>
          <p className="flex items-center gap-3 font-dash text-dash-xs font-bold text-indigo">
            <span aria-hidden="true" className="h-0.5 w-7 rounded-pill bg-indigo" />
            {t.curriculum.label}
          </p>
          <h3 className="mt-3 font-dash text-dash-xl font-extrabold text-ink">{t.curriculum.heading}</h3>
          <p className="mt-2 font-dash text-dash-sm text-ink-soft">{t.curriculum.body}</p>
        </div>
        <ul className="flex flex-wrap gap-2.5">
          {CURRICULUM_SKILLS.map(({ skill, dot }) => (
            <li
              key={skill}
              className="inline-flex items-center gap-2.5 rounded-pill border-2 border-line bg-surface py-2 pr-4 pl-3 font-dash text-dash-sm font-extrabold text-ink shadow-[0_3px_0_var(--color-line)]"
            >
              <span aria-hidden="true" className={`h-3 w-3 rounded-pill ${dot}`} />
              {t.skills[skill]}
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  );
}
